// Tolerant accessors + helpers for the ติดตาม / Ticket feature.
//
// ⚠️ The exact Xano `ticket` row shape is NOT yet verified (Phase 0 discovery —
// see docs/ticket-endpoint-notes.md). Every accessor below reads several plausible
// field names so the UI survives whatever shape comes back, the same way
// Recall.jsx's `r*` accessors tolerate both recall-record and patient rows.
// Once the real shape is known, tighten these.

/* ── date coercion: string | epoch-ms | {date/created_at/...} → ISO yyyy-mm-dd ── */
// Timestamps are rendered in Bangkok time, not UTC: a ticket created 02:00 Bangkok
// is still 19:00 UTC the previous day, and would otherwise display a day early.
const isoBkk = (ms) => new Date(ms).toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' })

export function dateOf(v) {
  if (v == null || v === '') return ''
  if (typeof v === 'object') return dateOf(v.date ?? v.created_at ?? v.timestamp ?? v.start_date ?? '')
  if (typeof v === 'number') return isoBkk(v)
  const s = String(v)
  if (/^\d{10,}$/.test(s)) return isoBkk(Number(s))
  return s.slice(0, 10)
}

/* ── yyyy-mm-dd arithmetic (UTC-anchored so no tz/DST drift) ─────────────────── */
export const todayBkk = () => isoBkk(Date.now())

const partsOf = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number)
  return (y && m && d) ? Date.UTC(y, m - 1, d) : NaN
}

export function addDays(iso, n) {
  const t = partsOf(iso)
  if (Number.isNaN(t)) return ''
  return new Date(t + n * 86400000).toISOString().slice(0, 10)
}

// same day-of-month n months on; clamps when the target month is shorter
// (31 Jan +1 month → 28/29 Feb, not 2/3 Mar).
export function addMonths(iso, n) {
  const t = partsOf(iso)
  if (Number.isNaN(t)) return ''
  const d = new Date(t)
  const day = d.getUTCDate()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() + n)
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()
  d.setUTCDate(Math.min(day, lastDay))
  return d.toISOString().slice(0, 10)
}

// whole days from a → b (b - a); null if either is unparseable
export function daysBetween(a, b) {
  const va = partsOf(a), vb = partsOf(b)
  if (Number.isNaN(va) || Number.isNaN(vb)) return null
  return Math.round((vb - va) / 86400000)
}

// epoch-ms | ISO | timestamp → epoch ms (for sorting / relative display); 0 if unknown
export function epochOf(v) {
  if (v == null || v === '') return 0
  if (typeof v === 'object') return epochOf(v.date ?? v.created_at ?? v.timestamp ?? '')
  if (typeof v === 'number') return v
  const s = String(v)
  if (/^\d{10,}$/.test(s)) return Number(s)
  const t = Date.parse(s)
  return Number.isNaN(t) ? 0 : t
}

export function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  if (!y) return iso
  return `${Number(d)}/${Number(m)}/${String(y).slice(2)}`
}

export const fmtDuration = (n) => (n == null ? '—' : n <= 0 ? 'วันนี้' : `${n} วัน`)

/* ── ticket-row accessors (t*) ──────────────────────────────────────────────── */
export const tId = (r) => r?.id ?? r?.ticket_id ?? null
export const tDetails = (r) => String(r?.details ?? r?.detail ?? r?.description ?? r?.title ?? r?.note ?? '').trim()
export const tDone = (r) => r?.done === true || r?.done === 1 || r?.status === 'done'
export const tType = (r) => String(r?.type ?? '').trim()
// A ticket row carries NO branch_id of its own — the branch comes from the joined
// appointment (present on every row sampled). Falls back to the treatment, then the
// patient's home branch; those can disagree with the appointment (~20% of rows), so
// the appointment wins: it's where the visit this ticket is about actually happened.
export const tBranchId = (r) => {
  const id = r?.appointment?.branch_id ?? r?.treatment?.branch_id ?? r?.patient?.branch_id ?? r?.branch_id ?? r?.branch?.id
  return id != null && id !== '' ? String(id) : ''
}
export const tAppointmentId = (r) => r?.appointment_id ?? r?.appointment?.id ?? 0
export const tTreatmentId = (r) => r?.treatment_id ?? r?.treatment?.id ?? 0
export const tCreatedAt = (r) => dateOf(r?.created_at)
export const tModifiedAt = (r) => dateOf(r?.modified_at)

// วันติดตาม — a plain yyyy-mm-dd string on the row. This is what Xano's
// start_date/end_date window filters on, and what the list is sorted by.
export const tDateFollowup = (r) => dateOf(r?.date_followup ?? r?.followup_date ?? '')

// how long the ticket has been alive, in whole days.
//   not done → created → today, and keeps growing.
//   done     → created → modified_at, frozen. modified_at is bumped by ANY edit,
//              so on a done ticket this approximates the completion date.
export function tAgeDays(r) {
  const from = tCreatedAt(r)
  if (!from) return null
  const to = (tDone(r) && tModifiedAt(r)) || todayBkk()
  const n = daysBetween(from, to)
  return n == null ? null : Math.max(0, n)
}

// an unfinished ticket whose วันติดตาม has already passed
export const tOverdue = (r) => {
  const f = tDateFollowup(r)
  return !!f && !tDone(r) && f < todayBkk()
}

// Human urgency for วันติดตาม, relative to today → { label, tone } | null.
// null for a done ticket: "เลยกำหนด 300 วัน" on something already finished is noise,
// so the เสร็จแล้ว view just shows the plain date.
export function tFollowupRel(r) {
  const f = tDateFollowup(r)
  if (!f || tDone(r)) return null
  const d = daysBetween(todayBkk(), f)
  if (d == null) return null
  if (d < 0) return { label: `เลยกำหนด ${Math.abs(d)} วัน`, tone: 'overdue' }
  if (d === 0) return { label: 'ครบวันนี้', tone: 'today' }
  return { label: `อีก ${d} วัน`, tone: 'future' }
}

export const REL_TONES = {
  overdue: { text: 'text-rose-600', dot: 'bg-rose-500' },
  today:   { text: 'text-amber-600', dot: 'bg-amber-500' },
  future:  { text: 'text-slate-400', dot: 'bg-slate-300' },
}
export const tCreator = (r) =>
  r?.created_by?.code || r?.created_by?.name ||
  (typeof r?.created_by === 'string' ? r.created_by : '') ||
  r?.creator?.code || r?.creator?.name || ''

// tags: r.tag / r.tags may be string[], object[] ({name}|{tag}|{label}), or a
// single string. Always return string[].
export function tTags(r) {
  const raw = r?.tag ?? r?.tags ?? []
  const arr = Array.isArray(raw) ? raw : (raw ? [raw] : [])
  return arr.map((t) => (typeof t === 'string' ? t : (t?.name ?? t?.tag ?? t?.label ?? t?.value ?? ''))).filter(Boolean)
}

// linked patient — flat fields OR a nested patient/_patient object
const pObj = (r) => r?.patient ?? r?._patient ?? r ?? {}
export const tPatientId = (r) => r?.patient_id ?? r?.patient?.id ?? r?._patient?.id ?? null
export function tPatientName(r) {
  const p = pObj(r)
  const joined = [p.prefix, p.name, p.surname].filter(Boolean).join(' ').trim()
  return String(joined || p.fullname || r?.fullname || r?.patient_name || '').trim()
}
export const tHN = (r) => { const p = pObj(r); return p.HN ?? p.hn ?? r?.HN ?? r?.hn ?? '' }
// patient.status — the CRM's own patient-status enum (see data/patientStatuses.js),
// e.g. 'Recall GP' | 'มีนัดแล้ว' | 'ปกส'. Not the ticket's own done flag.
export const tPatientStatus = (r) => String(r?.patient?.status ?? r?._patient?.status ?? '').trim()

/* ── joined appointment / treatment ─────────────────────────────────────────── */
// Ticket rows embed the related records, so the drawer needs no extra fetch.
// appointment: { date, branch_id, appointmentlist_id, doctor_id, details,
//                start_date, status, end_date, remark, title, from, doctor }
// treatment:   { branch_id, date, detail, doctor_id, paymentMethod, remark, doctor }
// doctor:      { name, name_eng, code, profile_img }
export const tAppointment = (r) => (r?.appointment && typeof r.appointment === 'object' ? r.appointment : null)
export const tTreatment = (r) => (r?.treatment && typeof r.treatment === 'object' ? r.treatment : null)

// doctor display name — `code` is the established convention elsewhere
// (lib/doctorFilter.js buildDocInfo), falling back to name / name_eng.
export const docName = (d) => String(d?.code || d?.name || d?.name_eng || '').trim()
export const docImg = (d) => String(d?.profile_img || '').trim()

// epoch ms → Bangkok HH:mm
export function fmtTimeBkk(ms) {
  const n = Number(ms)
  if (!n || Number.isNaN(n)) return ''
  return new Date(n).toLocaleTimeString('en-GB', { timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit' })
}

// embedded follow-up notes thread
export function tFollowUps(r) {
  const raw = r?.follow_up ?? r?.followup ?? r?.follow_ups ?? []
  return Array.isArray(raw) ? raw : []
}
export const fuId = (f) => f?.uuid ?? f?.id ?? null
export const fuDetails = (f) => String(f?.details ?? f?.detail ?? f?.note ?? '').trim()
export const fuDate = (f) => dateOf(f?.created_at ?? f?.date)
// `created_by` on a follow_up entry is an **Employee id (int)** — the embedded
// object is not joined, so unlike the ticket row there is no `creator` to read a
// code off. Callers resolve the id through /api/employees/:id. The object/string
// branches stay for the day Xano starts expanding it.
export const fuCreator = (f) =>
  f?.created_by?.code || f?.created_by?.name ||
  (typeof f?.created_by === 'string' ? f.created_by : '') || ''
export const fuCreatorId = (f) => f?.created_by?.id ?? (typeof f?.created_by === 'number' ? f.created_by : null)
export const fuEditedAt = (f) => {
  const c = f?.created_at, m = f?.modified_at
  return m && c && Number(m) - Number(c) > 60_000 ? dateOf(m) : null   // ignore the same-second stamp on create
}

/* ── branch relabel (UI-only; never rewrite branch_id) ──────────────────────── */
export const relabelBranch = (name) => (/สนามเป้า/.test(name || '') ? 'อารีย์' : name)

/* ── tag catalog normalizer — tolerates array | {items} | {tags} | {tag} ─────── */
// `choice` first: GET /ticket/tag returns Xano value-list objects keyed {choice}.
// It was missing here, which left the Tag dropdowns empty.
export function normalizeTagCatalog(r) {
  const raw = Array.isArray(r) ? r : (r?.items ?? r?.tags ?? r?.tag ?? [])
  const arr = Array.isArray(raw) ? raw : []
  return [...new Set(
    arr.map((t) => (typeof t === 'string' ? t : (t?.choice ?? t?.name ?? t?.tag ?? t?.label ?? t?.value ?? '')))
      .map((s) => String(s).trim())
      .filter(Boolean)
  )]
}
